import React from 'react';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import { HistoryDrawerState, HistoryEntry } from '../../types/ownerHistory';
import { useAppSelector } from '../../app/hooks';
import { useHistoryDrawer } from './useHistoryDrawer';
import ClockIcon from '../../assets/Svg/ClockIcon';
import Tooltip from '../_custom/Tooltip/Tooltip';
import styles from './HistoryDrawer.module.scss';
import { HISTORY_DRAWER_DISPLAY } from './constants';
import { resolveHistoryValue } from './historyLabelMap';

interface HistoryDrawerProps {
    open: boolean;
    onClose: () => void;
    referenceId: string;
    fieldKey: string;
    title: string;
    companyName?: string;
    // Optional stored-value -> display-label map (e.g. dropdown enum -> human label).
    // When a value isn't in the map (owners, unset), the raw label is used.
    valueLabelMap?: Record<string, string>;
    // Additive-only (GF-6): both absent ⇒ today's `account/getHistory` path, unchanged for
    // the Leads owner-history drawer.
    assesseeOrgId?: string;
    historySource?: HistoryDrawerState['historySource'];
}

// Renders the value chip; only wraps it in a hover tooltip when the text is actually
// clipped (scrollWidth > clientWidth), so short names don't show a redundant tooltip.
const HistoryValueBadge: React.FC<{ label: string; isNone: boolean }> = ({ label, isNone }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [truncated, setTruncated] = React.useState(false);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (el) setTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    const badge = (
        <div ref={ref} className={`${styles.EntryLabel} ${isNone ? styles.EntryLabelNone : ''}`}>
            {label}
        </div>
    );

    return truncated ? (
        <Tooltip content={label} direction="top" fitContent usePortal={true}>
            {badge}
        </Tooltip>
    ) : (
        badge
    );
};

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
    open,
    onClose,
    referenceId,
    fieldKey,
    title,
    companyName,
    valueLabelMap,
    assesseeOrgId,
    historySource,
}) => {
    const userList = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const { entries, loading, error, resolveUserName, getEntryMainLabel, formatEntryDate, getTimeSpent } = useHistoryDrawer({
        referenceId,
        fieldKey,
        open,
        assesseeOrgId,
        historySource,
    });

    const renderEntry = (entry: HistoryEntry, index: number, isLast: boolean) => {
        const nextEntry = !isLast ? entries[index + 1] : undefined;
        const timeSpent = getTimeSpent(entry, nextEntry);
        const formattedTime = formatEntryDate(entry.changeDate);
        // Resolve stored dropdown enum -> display label (incl. multi-select array/comma
        // values); fall back to the raw label (owners resolve via userList, unset stays "None").
        const rawLabel = getEntryMainLabel(entry, userList);
        const mainLabel = resolveHistoryValue(entry.currentValue, valueLabelMap) ?? rawLabel;
        const isNoneValue = mainLabel === HISTORY_DRAWER_DISPLAY.EMPTY_VALUE_DISPLAY;
        const changedBy = resolveUserName(entry.changeUserId, userList);

        return (
            <div key={`${entry.changeDate}-${index}`} className={styles.TimelineEntry}>
                <div className={styles.TimelineLeft}>
                    <div className={styles.EntryLabelRow}>
                        <HistoryValueBadge label={mainLabel} isNone={isNoneValue} />
                        <div className={styles.TimeSpent}>
                            <ClockIcon width="14" height="14" />
                            {timeSpent}
                        </div>
                    </div>
                </div>

                <div className={styles.TimelineRight}>
                    <div className={styles.EntryTime}>
                        {formattedTime} by {changedBy}
                    </div>
                </div>

                {!isLast && <div className={styles.TimelineConnector} />}
            </div>
        );
    };

    if (!open) return null;

    return (
        <SideDrawer width="40%" onClose={onClose} style={{ background: 'var(--hover-bg)' }}>
            <div className={styles.DrawerHeader}>
                <h2 className={styles.DrawerTitle}>{title}</h2>
                {companyName && (
                    <p className={styles.CompanyName} title={companyName}>
                        {companyName}
                    </p>
                )}
            </div>

            <div className={styles.DrawerContent}>
                {loading && (
                    <div className={styles.LoadingContainer}>
                        <div className={styles.LoadingSpinner} />
                    </div>
                )}

                {!loading && error && <div className={styles.EmptyState}>Failed to load history. Please try again.</div>}

                {!loading && !error && entries.length === 0 && <div className={styles.EmptyState}>No history available.</div>}

                {!loading && !error && entries.length > 0 && (
                    <div className={styles.Timeline}>
                        {entries.map((entry, index) => renderEntry(entry, index, index === entries.length - 1))}
                    </div>
                )}
            </div>
        </SideDrawer>
    );
};

export default HistoryDrawer;
